import React, { useEffect, useState, useRef } from 'react'

export default function ReviewSlider({ lang = 'da' }) {
  const [reviews, setReviews] = useState([])
  const [loading, setLoading] = useState(true)
  const scrollRef = useRef(null)

  useEffect(() => {
    async function fetchReviews() {
      try {
        const res = await fetch('https://api.dev.auxo.dk/public/v2/content/anmeldelser')
        if (res.ok) {
          const data = await res.json()
          if (data && data.items && data.items.length > 0) {
            setReviews(data.items)
            setLoading(false)
            return
          }
        }
      } catch (err) {
        console.warn('Could not load reviews from CMS, using fallback', err)
      }
      // Scraped reviews from Google Reviews for Borgs Køreskole
      setReviews([
        {
          id: '1',
          name: 'Corné Engsvang',
          rating: 5,
          review_text: 'Har haft en rigtig god oplevelse af denne forløb. Bedste køreskole i kalundborg! Rigtig gode teori timer hos Mette og en rigtig god forløb med at køre med Kim',
          course: 'Google-anmeldelse'
        },
        {
          id: '2',
          name: 'Matilde Larsen',
          rating: 5,
          review_text: 'Verdens bedste køreskole! Jeg vil uden tvivl anbefale Borgs køreskole til enhver person som kommer og spørger! Mette og Kim er de mest fantastiske mennesker',
          course: 'Google-anmeldelse'
        },
        {
          id: '3',
          name: 'Mohamed Gamal',
          rating: 5,
          review_text: 'Fantastisk køreskole. Hyggelige kørelærere der endda sætter mere tid af til undervisningen end hvad der er lovkrav. Teori er også nemmere at forstå, da der bliver brugt minibil-magneter, og generelt en meget god stemning 10/10.',
          course: 'Google-anmeldelse'
        },
        {
          id: '4',
          name: 'Barbara Sabljak',
          rating: 5,
          review_text: 'Jeg giver min varmeste anbefaling til Mette og Kim, jeg er 52 år, og skulle tage mit første kørekort, det har været en tryg oplevelse, Mette er eminent til at undervise.',
          course: 'Google-anmeldelse'
        },
        {
          id: '5',
          name: 'Lene Johansen',
          rating: 5,
          review_text: 'Super fantastiske kørelære og teorilære. Jeg er super glad for i kunne hjælpe mig så jeg kunne få det kørekort.',
          course: 'Google-anmeldelse'
        },
        {
          id: '6',
          name: 'Mathilde Knudsen',
          rating: 5,
          review_text: 'Fantastisk kørelærer! Jeg kom fra en anden køreskole, men fik hurtigt den støtte og læring, jeg manglede. Kan stærkt anbefales.',
          course: 'Google-anmeldelse'
        }
      ])
      setLoading(false)
    }

    fetchReviews()
  }, [])

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = scrollRef.current.clientWidth * 0.75
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      })
    }
  }

  return (
    <section className="py-20 bg-slate-100 text-slate-900 relative border-t border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with Title & Controls aligned on container line */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-6">
          <div>
            <h2 className="font-header text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              {lang === 'en' ? (
                <>What students say about <span className="text-emerald-700">Borgs Driving School</span></>
              ) : (
                <>Hvad siger eleverne hos <span className="text-emerald-700">Borgs Køreskole</span></>
              )}
            </h2>
            <p className="mt-3 text-slate-600 max-w-2xl text-base">
              {lang === 'en' 
                ? 'Read about experiences from students at Borgs Driving School throughout the years.'
                : 'Læs hvordan oplevelsen hos Borgs Køreskole har været hos de forskellige elever gennem tiden.'
              }
            </p>
          </div>

          {/* Controls + Google Reviews link */}
          <div className="flex flex-wrap items-center gap-4 shrink-0">
            <a
              href="https://www.google.com/search?q=Borgs+K%C3%B8reskole+Anmeldelser"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white hover:bg-slate-50 border border-slate-300 text-xs sm:text-sm font-semibold text-slate-700 transition-colors shadow-sm"
            >
              <i className="fa-brands fa-google text-emerald-600" aria-hidden="true"></i>
              <span>{lang === 'en' ? 'See more reviews' : 'Se flere anmeldelser'}</span>
            </a>

            <div className="flex items-center gap-2">
              <button
                onClick={() => scroll('left')}
                aria-label={lang === 'en' ? 'Previous reviews' : 'Forrige anmeldelser'}
                className="w-11 h-11 rounded-full bg-white hover:bg-emerald-600 border border-slate-200 hover:border-emerald-600 text-slate-700 hover:text-white flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow group cursor-pointer"
              >
                <i className="fa-solid fa-arrow-left text-base group-hover:-translate-x-0.5 transition-transform" aria-hidden="true"></i>
              </button>
              <button
                onClick={() => scroll('right')}
                aria-label={lang === 'en' ? 'Next reviews' : 'Næste anmeldelser'}
                className="w-11 h-11 rounded-full bg-white hover:bg-emerald-600 border border-slate-200 hover:border-emerald-600 text-slate-700 hover:text-white flex items-center justify-center transition-all duration-200 shadow-sm hover:shadow group cursor-pointer"
              >
                <i className="fa-solid fa-arrow-right text-base group-hover:translate-x-0.5 transition-transform" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable track aligned inside container */}
        <div
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto scroll-smooth py-2 no-scrollbar"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {reviews.map((rev, idx) => (
            <div
              key={`${rev.id || idx}-${idx}`}
              className="w-[300px] sm:w-[360px] md:w-[420px] flex-shrink-0 bg-white p-6 border border-slate-200/80 shadow-sm hover:shadow-md hover:border-emerald-500/50 transition-all duration-300 flex flex-col justify-between"
            >
              <div>
                {/* Rating stars */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-1 text-amber-400">
                    {[...Array(rev.rating || 5)].map((_, i) => (
                      <i key={i} className="fa-solid fa-star text-sm" aria-hidden="true"></i>
                    ))}
                  </div>
                  <i className="fa-solid fa-quote-right text-emerald-100 text-2xl" aria-hidden="true"></i>
                </div>

                {/* Review text */}
                <p className="text-slate-700 text-sm sm:text-base leading-relaxed mb-6 italic whitespace-pre-line">
                  "{rev.review_text}"
                </p>
              </div>

              {/* Author info */}
              <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                <div>
                  <h3 className="font-header text-slate-900 font-bold text-base leading-none">
                    {rev.name}
                  </h3>
                  <span className="text-emerald-700 font-medium text-xs mt-1 block">
                    {rev.course || (lang === 'en' ? 'Student at Borgs Driving School' : 'Elev hos Borgs Køreskole')}
                  </span>
                </div>
                <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold text-xs border border-emerald-200">
                  {rev.name ? rev.name.charAt(0) : 'E'}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
